import _ from 'lodash';
import Moment from 'moment';

import Coverages from './comparisons/statics/coverages.statics.json';
import CoveragesV6 from './comparisons/statics/coverages.statics.v6.json';
import Globals from './comparisons/statics/globals.static.json';
import type { ProductType } from '../../../types';
import { ProductTypes } from '../../../types';
import { formatCurrencyToTenthsPlaceWithUnit } from '../../../utils/currency.utils';
import { DATE_FORMAT, toUniversalUtcDate } from '../../../utils/date.utils';
import { PolicyStatus } from '../../_statics/policy.statics';
import { getConsolidatedP100CoverageKeys } from '../../workflows/quotes/prime-100/utils/coverages';
import { getFtfSeCoveragePayload } from '../../workflows/quotes/prime-100/hooks/useGetCalculator';
import { Prime100Coverages } from '../../workflows/quotes/prime-100/types';
import { BoostRaterVersionResponse } from '../../workflows/quotes/prime-100/api/types';
import type { FirmographicDataDto } from '../../../types/firmo/firmographic-data.dto';

const limitEquivalentCoverages = [
  'securityBreachExpense',
  'securityBreachLiability',
  'restorationOfElectronicsData',
  'computerFraudEndorsement',
  'pciFinesAndPenalties',
  'regulatorDefenceAndPenalties',
];
const percentLimit = ['extortionThreats', 'publicRelationsExpense'];

export const retroMappingFull = {
  1: '1 Year',
  2: '2 Years',
  3: 'Full Prior Acts', // P100
  '-1': 'Full Prior Acts', //  P250
};

export const retroMappingShort = {
  1: '1 Yr',
  2: '2 Yrs',
  3: 'Full', // P100
  '-1': 'Full', // P250
};

export function generateCoverageText(data: any) {
  const coveragesAdded = _.get(data, 'coveragesAdded', []);
  const coveragesRemoved = _.get(data, 'coveragesRemoved', []);
  const coveragesUpdated = _.get(data, 'coveragesUpdated', []);

  const added = _.get(coveragesAdded, 'length', 0);
  const removed = _.get(coveragesRemoved, 'length', 0);
  const updated = _.get(coveragesUpdated, 'length', 0);

  let coverageText = 'Updated';
  const isAdded = added > 0 && removed === 0 && updated === 0;
  const isRemoved = added === 0 && removed > 0 && updated === 0;
  const isNone = added === 0 && removed === 0 && updated === 0;

  if (isAdded) {
    coverageText = 'Added';
  } else if (isRemoved) {
    coverageText = 'Removed';
  } else if (isNone) {
    coverageText = 'None';
  }

  return coverageText;
}

export function generateInsuredInformationText(data: any) {
  const oldData = _.get(data, 'firmographicData', []);
  let coverageText = 'None';

  if (oldData.name !== data.companyName) {
    coverageText = 'Updated';
  }

  return coverageText;
}

function checkIsAfter(date1: any, date2: any) {
  return Moment(date1).isAfter(date2);
}

export function generatePolicyDurationText(data: any) {
  const oldEndDate = toUniversalUtcDate(data?.initialRequestData?.endDate);
  const newEndDate = toUniversalUtcDate(data?.expiresOn);

  let coverageText = 'None';

  const isUpdated = checkIsAfter(newEndDate, oldEndDate);

  if (isUpdated) {
    coverageText = 'Updated';
  }

  return coverageText;
}

export function getQuotePayload(
  latestPolicydata: any,
  originalPolicydata: any,
  agencyData: any,
  loggedInAgent: any,
  product: any,
  isCowbellJumpedIntoAgency: any
) {
  if (!latestPolicydata) {
    return;
  }

  const coverage = getCoverages(latestPolicydata, product);

  // pick up agent on account from broker of record object if there is BOR processed
  let agentOnAccount = _.pick(latestPolicydata, [
    'agentFirstName',
    'agentLastName',
    'agentEmail',
    'agentPhone',
  ]);

  if (
    originalPolicydata.isBOR &&
    !_.isEmpty(originalPolicydata.brokerOfRecord)
  ) {
    agentOnAccount = _.pick(originalPolicydata.brokerOfRecord, [
      'agentFirstName',
      'agentLastName',
      'agentEmail',
      'agentPhone',
    ]);
  }
  const agent = getAgent(
    agentOnAccount,
    agencyData,
    loggedInAgent,
    isCowbellJumpedIntoAgency
  );

  return {
    ...coverage,
    ...agent,
    teamIds:
      originalPolicydata.isBOR && !_.isEmpty(originalPolicydata.brokerOfRecord)
        ? [originalPolicydata.brokerOfRecord?.teamId ?? '']
        : originalPolicydata.teamIds ?? [],
  };
}

export function getCoverages(latestPolicyData: any, product: any) {
  let coverage;
  if (product === ProductTypes.p100) {
    const pickedCoverage = _.pick(latestPolicyData, [
      'sublimit',
      ...getConsolidatedP100CoverageKeys(),
    ]);
    coverage = {
      ...pickedCoverage,
      ...getFtfSeCoveragePayload(
        pickedCoverage.fundsTransferFraudCoverageLimit,
        pickedCoverage.fundsTransferFraudCoverageDeductible
      ),
    };
  }

  if (product === ProductTypes.p250) {
    coverage = {
      ..._.pick(latestPolicyData.initialRequestData, [
        'limit',
        'deductible',
        'coverages',
      ]),
    };
  }

  let effectiveDate = Moment().startOf('day').format(DATE_FORMAT); // endorsement effective date
  if (latestPolicyData.status === PolicyStatus.EXPIRED_STATUS) {
    effectiveDate = Moment.utc(latestPolicyData.endDate).format(DATE_FORMAT);
  }
  return {
    ...getCommonPolicyDetails(latestPolicyData),
    coverages: { ...coverage },
    effectiveDate,
  };
}

function getAgent(
  agentOnAccount: any,
  agencyData: any,
  loggedInAgent: any,
  isCowbellJumpedIntoAgency: any
) {
  let agentDetails = _.pick(loggedInAgent, [
    'firstName',
    'lastName',
    'email',
    'phone',
  ]);

  if (isCowbellJumpedIntoAgency) {
    if (_.isUndefined(agentOnAccount.agentEmail)) {
      if (!_.isUndefined(agencyData.contactEmail)) {
        agentDetails = {
          firstName: agencyData.contactFirstName,
          lastName: agencyData.contactLastName,
          email: agencyData.contactEmail,
          phone: agencyData.contactPhone,
        };
      }
    } else {
      agentDetails = {
        firstName: agentOnAccount.agentFirstName,
        lastName: agentOnAccount.agentLastName,
        email: agentOnAccount.agentEmail,
        phone: agentOnAccount.agentPhone,
      };
    }
  }

  return agentDetails;
}

export function getAdditionalInsuredsFromPolicyOrQuote(policyOrQuoteData: any) {
  return (
    policyOrQuoteData?.initialRequestData?.additionalInsureds ??
    policyOrQuoteData?.initial250RequestData?.additionalInsureds ??
    policyOrQuoteData?.additionalInsureds ??
    policyOrQuoteData?.additionalInsured ??
    []
  );
}

function getCommonPolicyDetails(latestPolicyData: any) {
  return {
    ..._.pick(latestPolicyData, [
      'premium',
      'totalPremium',
      'brokerFee',
      'accountId',
      'mgaFee',
    ]),
    policyId: latestPolicyData.id,
    endDate: Moment.utc(_.get(latestPolicyData, 'endDate')).format(DATE_FORMAT),
    additionalInsureds:
      getAdditionalInsuredsFromPolicyOrQuote(latestPolicyData),
    insuredInformation: {
      name: latestPolicyData.companyName,
      state: latestPolicyData?.firmographicData?.state?.toUpperCase(),
      ..._.pick(latestPolicyData.firmographicData, [
        'address1',
        'address2',
        'city',
        'zipCode',
      ]),
    },
  };
}

function buildIndustryInfo(firmographicData: FirmographicDataDto) {
  return {
    naicsCode: firmographicData.naicsCode,
    primaryIndustry: firmographicData.primaryIndustry,
    secondaryIndustry: firmographicData.secondaryIndustry,
    primaryNaicsPercentage: firmographicData.primaryNaicsPercentage,
    secondNaicsCode: firmographicData.secondNaicsCode,
    secondPrimaryIndustry: firmographicData.secondPrimaryIndustry,
    secondSecondaryIndustry: firmographicData.secondSecondaryIndustry,
    sicCode: firmographicData.sicCode,
    sicPrimaryDesc: firmographicData.sicPrimaryDesc,
    sicSecondaryDesc: firmographicData.sicSecondaryDesc,
  };
}

function buildOwnershipInfo(firmographicData: FirmographicDataDto) {
  return {
    ownershipType: firmographicData.ownershipType,
    entityType: firmographicData.entityType,
    changeInScope: firmographicData.changeInScope,
    changeInScopeDetail: firmographicData.changeInScopeDetail,
    isFranchise: firmographicData.isFranchise,
    franchiseSystemConnectivity: firmographicData.franchiseSystemConnectivity,
  };
}

export type InsuredFirmographics = ReturnType<
  typeof buildInsuredFirmographics
>['insuredFirmographics'];

export function buildInsuredFirmographics(
  firmographicData: FirmographicDataDto
) {
  return {
    insuredFirmographics: {
      ...buildAccountInfo(firmographicData),
      ...buildDetailsInfo(firmographicData),
      ...buildDomainInfo(firmographicData),
      ...buildIndustryInfo(firmographicData),
      ...buildOwnershipInfo(firmographicData),
      policyContact: buildOrganizationContactInfo(firmographicData),
      securityContact: buildSecurityContactInfo(firmographicData),
      mailingAddress: {
        address1: firmographicData.mailingAddress?.address1,
        address2: firmographicData.mailingAddress?.address2,
        address3: firmographicData.mailingAddress?.address3,
        city: firmographicData.mailingAddress?.city,
        state: firmographicData.mailingAddress?.state?.toUpperCase(),
        zipCode: firmographicData.mailingAddress?.zipCode,
        country: firmographicData.mailingAddress?.country,
      },
    },
  };
}

function buildAccountInfo(firmographicData: FirmographicDataDto) {
  return {
    name: firmographicData.name,
    dbaOrTradestyle: firmographicData.dbaOrTradestyle,
    address1: firmographicData.address1,
    address2: firmographicData.address2,
    address3: firmographicData.address3,
    city: firmographicData.city,
    state: firmographicData.state?.toUpperCase(),
    zipCode: firmographicData.zipCode,
    country: firmographicData.country,
    phoneNumber: firmographicData.phoneNumber,
  };
}

function buildDetailsInfo(firmographicData: FirmographicDataDto) {
  return {
    revenue: firmographicData.revenue,
    otherRevenue: firmographicData.otherRevenue,
    noOfEmployeesAll: firmographicData.noOfEmployeesAll,
    yearEstablished: firmographicData.yearEstablished,
    numberOfPersonalRecords: firmographicData.numberOfPersonalRecords,
    ein: firmographicData.ein,
    dunsNumber: firmographicData.dunsNumber,
  };
}

function buildDomainInfo(firmographicData: FirmographicDataDto) {
  const transformedDomains = firmographicData.domains?.join(', ');
  return {
    domainName: firmographicData.domainName,
    domains: transformedDomains,
    url: firmographicData.url,
  };
}

export function buildOrganizationContactInfo(
  firmographicData: FirmographicDataDto
) {
  return {
    firstName: firmographicData.customerFirstName,
    lastName: firmographicData.customerLastName,
    emailAddress: firmographicData.customerEmail,
    phoneNumber: firmographicData.customerPhoneNumber,
  };
}

export function buildSecurityContactInfo(
  firmographicData: FirmographicDataDto
) {
  return {
    firstName: firmographicData.securityContact?.firstName,
    lastName: firmographicData.securityContact?.lastName,
    email: firmographicData.securityContact?.email,
    phoneNumber: firmographicData.securityContact?.phoneNumber,
    title: firmographicData.securityContact?.title,
    company: firmographicData.securityContact?.company,
  };
}

export function getPolicyCoverages(data: any) {
  return {
    ..._.pick(data?.coverages, [
      'businessIncomeCoverage',
      'sublimit',
      'ransomPaymentEndorsement',
      'ransomPaymentLimit',
      'hardwareReplCostEndorsement',
      'hardwareReplCostSubLimit',
      'telecomsFraudEndorsement',
      'telecomsFraudSubLimit',
      'postBreachRemediationEndorsement',
      'postBreachRemediationSubLimit',
      'computerFraudEndorsement',
      'websiteMediaContentLiabilitySubLimit',
      'socialEngLimit',
      'socialEngDeductible',
      'socialEngEndorsement',
      'limit',
      'deductible',
      'waitingPeriod',
      'premium',
      'totalPremium',
      'brokerFee',
      'accountId',
      'teamIds',
    ]),
    websiteMediaContentLiabilityEndorsement: _.get(
      data,
      'websiteMediaContentLiabilityEndorsement',
      false
    ),
    retroActivePeriod: _.get(
      data,
      'retroactivePeriod',
      _.get(data, 'coverages.retroactivePeriod', '-')
    ),
    policyId: data?.id,
    endDate: (data?.endDate
      ? Moment.utc(data.endDate)
      : Moment.utc().add(1, 'years')
    ).format(DATE_FORMAT),
  };
}

/* This now corresponds to P100 V5 Coverages */

export function getCoverageFormat(data: any) {
  const { limit, additionalInsureds, additionalNamedInsured } = data;

  const mappedCoverages = Coverages.map((coverage) => {
    const coverageKey = data[coverage.covLabel];
    const subLimit = data[coverage.sublimit];
    const subDeductible = data[coverage.subDeductible];

    let finalLimit = subLimit;
    if (limitEquivalentCoverages.includes(coverage.covLabel)) {
      finalLimit = limit;
    } else if (percentLimit.includes(coverage.covLabel)) {
      finalLimit = 0.05 * limit;
    } else if (!coverageKey) {
      finalLimit = '-';
    }

    if (coverage.endorsement) {
      if (coverage.key === Prime100Coverages.ADDITIONAL_INSURED) {
        return {
          ...coverage,
          selected: !_.isEmpty(additionalInsureds),
        };
      }

      if (coverage.key === Prime100Coverages.ADDITIONAL_NAMED_INSURED) {
        return {
          ...coverage,
          selected: !_.isEmpty(additionalNamedInsured),
        };
      }
    }
    const isCoverageSelected = coverage.selected
      ? coverage.selected
      : coverageKey;

    const getWaitingPeriod = () => {
      if (coverage.key === Prime100Coverages.TRIA || !isCoverageSelected) {
        return;
      }

      return data.waitingPeriod;
    };

    const getRetroActivePeriod = () => {
      if (coverage.key === Prime100Coverages.TRIA || !isCoverageSelected) {
        return;
      }

      return data.retroactivePeriod;
    };

    return {
      ...coverage,
      selected: isCoverageSelected,
      limit:
        typeof finalLimit === 'number'
          ? formatCurrencyToTenthsPlaceWithUnit(finalLimit)
          : '-',
      deductible:
        coverageKey && subDeductible
          ? formatCurrencyToTenthsPlaceWithUnit(subDeductible)
          : '-',
      waitingPeriod: getWaitingPeriod(),
      retroActivePeriod: getRetroActivePeriod(),
    };
  });

  return getGlobalCoveragesFormat(data, mappedCoverages);
}

export const isBoostV6 = (version: string | undefined): boolean =>
  version === BoostRaterVersionResponse.V6 ||
  (typeof version === 'string' && version.toUpperCase() === 'V6');

/* This now corresponds to P100 V6 Coverages and mostly mirrors V5's getCoverageFormat */
export function getCoverageFormatV6(data: any) {
  const { limit, additionalInsureds, additionalNamedInsured } = data;

  const mappedCoverages = CoveragesV6.map((coverage) => {
    const coverageKey = data[coverage.covLabel];
    const subLimit = data[coverage.sublimit];
    const subDeductible = data[coverage.subDeductible];

    let finalLimit = subLimit;
    if (limitEquivalentCoverages.includes(coverage.covLabel)) {
      finalLimit = limit;
    } else if (!coverageKey && !coverage.selected) {
      finalLimit = '-';
    }

    if (coverage.endorsement) {
      if (coverage.key === Prime100Coverages.ADDITIONAL_INSURED) {
        return {
          ...coverage,
          selected: !_.isEmpty(additionalInsureds),
        };
      }

      if (coverage.key === Prime100Coverages.ADDITIONAL_NAMED_INSURED) {
        return {
          ...coverage,
          selected: !_.isEmpty(additionalNamedInsured),
        };
      }
    }
    const isCoverageSelected = coverage.selected
      ? coverage.selected
      : coverageKey;

    const getWaitingPeriod = () => {
      if (coverage.key === Prime100Coverages.TRIA || !isCoverageSelected) {
        return;
      }

      return data.waitingPeriod;
    };

    const getRetroActivePeriod = () => {
      if (coverage.key === Prime100Coverages.TRIA || !isCoverageSelected) {
        return;
      }

      return data.retroactivePeriod;
    };

    return {
      ...coverage,
      selected: isCoverageSelected,
      limit:
        typeof finalLimit === 'number'
          ? formatCurrencyToTenthsPlaceWithUnit(finalLimit)
          : '-',
      deductible:
        coverageKey && subDeductible
          ? formatCurrencyToTenthsPlaceWithUnit(subDeductible)
          : '-',
      waitingPeriod: getWaitingPeriod(),
      retroActivePeriod: getRetroActivePeriod(),
    };
  });

  return getGlobalCoveragesFormat(data, mappedCoverages);
}

export function getGlobalCoveragesFormat(data: any, mappedCoverages: any) {
  const mapCoverages = Globals.map((coverage) => {
    const coverageKey = data[coverage.covLabel];

    let formattedValue =
      typeof coverageKey === 'number'
        ? formatCurrencyToTenthsPlaceWithUnit(coverageKey)
        : '-';
    if (coverage.covLabel === 'waitingPeriod') {
      formattedValue = `${_.get(data, 'waitingPeriod')} Hours`;
    }
    if (coverage.covLabel === 'retroactivePeriod') {
      formattedValue = retroMappingFull[coverageKey];
    }

    return {
      ...coverage,
      value: coverageKey,
      formattedValue,
    };
  });

  return mappedCoverages.concat(mapCoverages);
}

export function getKeysForUpdatedCoverages(data1: any, data2: any) {
  const finalCoverages: any = [];
  data2.map((coverage: any) => {
    const { limit, deductible, selected, _id, covLabel, groupId, value } =
      coverage;
    const ID = _.findKey(data1, ['_id', _id]);

    if (!ID) {
      return null;
    }

    if (groupId === '0') {
      const previousValue = data1[ID].value;

      if (previousValue !== value) {
        finalCoverages.push(covLabel);
      }
    } else {
      const previousSelected = data1[ID].selected;
      const previousLimit = data1[ID].limit;
      const previousDeductible = data1[ID].deductible;

      const currentSelected = _.isUndefined(selected) ? false : selected;

      if (!previousSelected && !currentSelected) {
        return;
      }
      if (previousSelected !== currentSelected) {
        finalCoverages.push(covLabel);
      } else if (previousSelected === currentSelected) {
        if (previousLimit !== limit || previousDeductible !== deductible) {
          finalCoverages.push(covLabel);
        }
      }
    }
    return null;
  });
  return finalCoverages;
}

export function getDiffCoverages(summedUpKeys: any, coverages: any) {
  if (_.isEmpty(coverages)) {
    return [];
  }
  return summedUpKeys.map((covLabel: any) => {
    const ordinal = _.findKey(coverages, ['covLabel', covLabel]);

    return coverages[ordinal];
  });
}

export function getDiffCoveragesSummary(summedUpKeys: any, coverages: any) {
  return summedUpKeys.map((covLabel: any) => {
    return Object.values(coverages).find(
      (endorsementCov) =>
        // @ts-expect-error TS(2571): Object is of type 'unknown'.
        endorsementCov.covLabel.toLowerCase() === covLabel.toLowerCase()
    );
  });
}

export function getConsolidatesKeys(data: any) {
  const coveragesAdded = data?.coveragesAdded ?? [];
  const coveragesRemoved = data?.coveragesRemoved ?? [];
  const coveragesUpdated = data?.coveragesUpdated ?? [];

  return [...coveragesAdded, ...coveragesRemoved, ...coveragesUpdated];
}

export function generateAIText(data: any) {
  const coveragesAdded = data?.coveragesAdded ?? [];
  const coveragesRemoved = data?.coveragesRemoved ?? [];
  let text = 'None';

  if (coveragesAdded.includes('additionalInsureds')) {
    text = 'Added';
  } else if (coveragesRemoved.includes('additionalInsureds')) {
    text = 'Removed';
  }

  return text;
}

export function getAddressInfo(policy: any) {
  return _.pick(policy, ['address1', 'address2', 'city', 'state', 'zipCode']);
}

export function buildFirmographicsPayload(
  insuredFirmographics: InsuredFirmographics,
  additionalNamedInsured: string[]
) {
  return {
    firmographics: {
      ..._.omit(insuredFirmographics, [
        'address1',
        'address2',
        'address3',
        'city',
        'state',
        'zipCode',
        'country',
        'revenue',
        'otherRevenue',
        'domains',
      ]),
      domesticRevenue: insuredFirmographics.revenue,
      foreignRevenue: insuredFirmographics.otherRevenue,
      otherDomains: transformDomains(insuredFirmographics.domains),
      physicalAddress: {
        address1: insuredFirmographics.address1,
        address2: insuredFirmographics.address2,
        address3: insuredFirmographics.address3,
        city: insuredFirmographics.city,
        state: insuredFirmographics.state,
        zipCode: insuredFirmographics.zipCode,
        country: insuredFirmographics.country,
      },
      additionalNamedInsured,
    },
  };
}

export function transformDomains(domains: string | undefined): string[] {
  return (domains ?? '').split(',').map((domain: string) => domain.trim());
}

export const P100_DISABLED_FIELDS = [
  'state',
  'phoneNumber', // business phone number
  'detailsForm',
  'industryForm',
  'ownershipForm',
  'domainForm',
  'securityContactForm',
];

export const P250_DISABLED_FIELDS = [
  'changeInScope',
  'changeInScopeDetail',
  'primaryIndustry',
  'state',
  'yearEstablished',
];

export function getDisabledFieldsByProduct(product: ProductType) {
  if (product === ProductTypes.p100) {
    return P100_DISABLED_FIELDS;
  }
  if (product === ProductTypes.p250) {
    return P250_DISABLED_FIELDS;
  }

  return [];
}
